type MessageListener = (message: unknown) => void;

interface WSClient {
  subscribe: (workspaceId: string) => void;
  unsubscribe: (workspaceId: string) => void;
  onMessage: (listener: MessageListener) => () => void;
  connect: () => void;
  close: () => void;
}

function createWSClient(url: string): WSClient {
  let ws: WebSocket | null = null;
  let pingInterval: ReturnType<typeof setInterval> | null = null;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  const subscriptions = new Set<string>();
  const listeners = new Set<MessageListener>();

  function connect(): void {
    ws = new WebSocket(url);

    ws.onopen = () => {
      console.log("Connected to WS server");

      for (const workspaceId of subscriptions) {
        ws!.send(JSON.stringify({ type: "subscribe", workspaceId }));
      }

      pingInterval = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    ws.onmessage = (event: MessageEvent) => {
      const message: unknown = JSON.parse(event.data);

      if (
        typeof message === "object" &&
        message !== null &&
        "type" in message &&
        message.type === "pong"
      ) {
        return;
      }

      for (const listener of listeners) {
        listener(message);
      }
    };

    ws.onclose = () => {
      if (pingInterval) clearInterval(pingInterval);
      console.log("Disconnected, reconnecting...");
      reconnectTimeout = setTimeout(connect, 3000);
    };

    ws.onerror = (error: Event) => {
      console.error("WS error:", error);
    };
  }

  function subscribe(workspaceId: string): void {
    subscriptions.add(workspaceId);
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "subscribe", workspaceId }));
    }
  }

  function unsubscribe(workspaceId: string): void {
    subscriptions.delete(workspaceId);
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "unsubscribe", workspaceId }));
    }
  }

  function onMessage(listener: MessageListener): () => void {
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }

  function close(): void {
    if (pingInterval) clearInterval(pingInterval);
    if (reconnectTimeout) clearTimeout(reconnectTimeout);
    ws?.close();
  }

  return { subscribe, connect, unsubscribe, onMessage, close };
}

export type { WSClient, MessageListener };

export { createWSClient };
